import Box from '@redbubble/design-system/src/Box';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import React from 'react';
import styles from './ArtistProfile.module.scss';
import { ArtistMetricsProps } from './types';

const messages = defineMessages({
  followers: {
    defaultMessage: `{count, plural,
       one {Follower}
       other {Followers}}`,
  },
  following: {
    defaultMessage: 'Following',
  },
  favorited: {
    defaultMessage: 'Favorited',
  },
});

const formatWithSiPrefix = (num: number): string => {
  if (num > 1000000) {
    return `${(Math.round(num / 10000) / 100).toLocaleString()}M`;
  }
  if (num > 10000) {
    return `${(Math.round(num / 100) / 10).toLocaleString()}k`;
  }
  return num.toLocaleString();
};

interface MetricProps {
  count: number;
  label: string;
  href?: string;
  hideBorder?: boolean;
}

const Metric: React.FC<MetricProps> = ({
  count,
  label,
  href,
  hideBorder = false,
}) => {
  const content = (
    <Box>
      <Box display="block">
        <Text type="display3" title={String(count)}>
          {formatWithSiPrefix(count)}
        </Text>
      </Box>
      <Box>
        <Text type="body">{label}</Text>
      </Box>
    </Box>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      className={styles.metric}
      style={hideBorder ? undefined : { borderLeft: '1px solid #D6DADF' }}
    >
      {href ? (
        <a className={styles.profileLink} href={href}>
          {content}
        </a>
      ) : (
        content
      )}
    </Box>
  );
};

export const ArtistMetrics: React.FC<ArtistMetricsProps> = ({
  username,
  followerCount,
  followingCount,
  favoriteReceivedCount,
}) => {
  const intl = useIntl();

  return (
    <Box
      marginTop="xl"
      className={styles.metrics}
      display="flex"
      flexDirection="row"
    >
      <Metric
        count={followerCount}
        label={intl.formatMessage(messages.followers, {
          count: followerCount,
        })}
        href={`/people/${username}/followers`}
        hideBorder
      />
      <Metric
        count={followingCount}
        label={intl.formatMessage(messages.following)}
        href={`/people/${username}/following`}
      />
      <Metric
        count={favoriteReceivedCount}
        label={intl.formatMessage(messages.favorited)}
      />
    </Box>
  );
};
